/** OPTION A — Executive Minimal. Private-banking restraint. */
import { ArrowRight, Loader2, ChevronLeft } from "lucide-react";
import { useLoginAuth } from "./useLoginAuth";
import LifecycleSignature from "@/components/app/LifecycleSignature";

export default function OptionA() {
  const a = useLoginAuth();
  return (
    <div className="min-h-screen bg-[color:var(--st-bg)] text-[color:var(--st-text)]" data-testid="login-option-a">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-16 px-8 pt-16 pb-10">
        {/* Brand */}
        <div className="lg:col-span-7 flex flex-col justify-between min-h-[70vh]">
          <div>
            <div className="text-[13px] font-semibold tracking-[0.14em] text-[color:var(--st-text)]">SUNTRONIX ONE</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)] mt-1">
              Global Technology Operations Platform
            </div>
          </div>
          <div className="max-w-xl">
            <h1 className="font-heading text-[52px] leading-[1.03] tracking-[-0.02em] text-[color:var(--st-text)] font-medium">
              Your technology estate.<br />
              <span className="text-[color:var(--st-text-muted)] font-normal">Managed as one.</span>
            </h1>
            <p className="mt-6 text-[15px] leading-[1.7] text-[color:var(--st-text-muted)] max-w-[38ch]">
              One platform connecting every technology asset, employee and lifecycle stage.
            </p>
          </div>
          <LifecycleSignature variant="full" />

        </div>

        {/* Auth */}
        <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[color:var(--st-border)] flex items-center">
          <div className="w-full max-w-[380px]">
            <h2 className="font-heading text-[22px] font-medium tracking-[-0.01em]">Welcome to ONE</h2>
            <p className="text-[13px] text-[color:var(--st-text-muted)] mt-1">
              Access your organisation&apos;s technology workspace.
            </p>
            {a.step === "email" ? (
              <form onSubmit={a.continueEmail} className="mt-8 space-y-4">
                <Field label="Work email" value={a.email} onChange={a.setEmail} type="email" required autoFocus />
                {a.error && <ErrorMsg msg={a.error} />}
                <button type="submit" disabled={a.loading || !a.email} className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="a-continue">
                  {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4" strokeWidth={1.6} /></>)}
                </button>
              </form>
            ) : (
              <form onSubmit={a.submitPassword} className="mt-8 space-y-4">
                <button type="button" onClick={a.backToEmail} className="text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
                  <ChevronLeft className="h-3.5 w-3.5" /> {a.email}
                </button>
                <Field label="Password" value={a.password} onChange={a.setPassword} type="password" required autoFocus />
                {a.error && <ErrorMsg msg={a.error} />}
                <button type="submit" disabled={a.loading} className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <footer className="border-t border-[color:var(--st-border)] py-5">
        <div className="max-w-6xl mx-auto px-8 flex justify-between text-[11px] text-[color:var(--st-text-subtle)]">
          <span>© 2026 Suntronix</span>
          <div className="flex gap-6">
            <a href="/help/trust">Security &amp; Privacy</a><a href="/help/accessibility">Accessibility</a><a href="/help/status">System status</a><a href="/help">Help</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", ...p }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">{label}</span>
      <input {...p} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[15px] focus:outline-none focus:border-[color:var(--st-text)] focus:ring-2 focus:ring-[color:var(--st-text)]/10" />
    </label>
  );
}
function ErrorMsg({ msg }) { return <div role="alert" className="text-[12px] text-[color:var(--st-critical)] bg-[color:var(--st-critical-soft)] border border-[color:var(--st-critical-soft)] rounded-md px-3 py-2">{msg}</div>; }
